import type { LocationEntry, Provider } from '@latency-globe/shared';
import type { Theme } from '@latency-globe/shared';

export type RenderMode = 'field' | 'boundaries';

export interface CellInfo {
  cell: string;
  code: string;
  score: number;
  total: number;
  runnerUp: string | null;
  boot: number;
}

export interface State {
  provider: Provider;
  mode: RenderMode;
  theme: Theme;
  build: number;
  /** locations by provider, keyed by code */
  locations: Partial<Record<Provider, Map<string, LocationEntry>>>;
  selected: CellInfo | null;
  hovered: { info: CellInfo; x: number; y: number } | null;
  banner: string | null;
}

type Listener = (s: State, changed: Set<keyof State>) => void;

export class Store {
  private listeners = new Set<Listener>();
  constructor(public state: State) {}
  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }
  set(patch: Partial<State>): void {
    const changed = new Set<keyof State>();
    for (const k of Object.keys(patch) as Array<keyof State>) {
      if (this.state[k] !== patch[k]) { (this.state as unknown as Record<string, unknown>)[k] = patch[k]; changed.add(k); }
    }
    if (changed.size) for (const fn of this.listeners) fn(this.state, changed);
  }
  locationsFor(provider = this.state.provider): Map<string, LocationEntry> {
    return this.state.locations[provider] ?? new Map();
  }
}

export function systemTheme(): Theme {
  return matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

export function reducedMotion(): boolean {
  return matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** §9.7 — these force the accessible render mode. */
export function forcedBoundaries(): boolean {
  return matchMedia('(prefers-contrast: more)').matches || matchMedia('(forced-colors: active)').matches;
}
